import classNames from 'classnames';
import React, {ChangeEvent, KeyboardEvent, useEffect, useRef, useState} from 'react';
import Button from '../../../../incase-domain/components/button/Button';

export interface NumberInputProps {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    onBlur?: (value: number | undefined) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    className?: string;
    validator?: (val: number) => boolean;
    disabled?: boolean;
    dataCy?: string;
    leading?: string;
    trailing?: JSX.Element;
    min?: number;
    step?: number;
    max?: number;
}

function NumberInput({
    value,
    onChange,
    onBlur,
    validator,
    onKeyUp,
    placeholder,
    className,
    min,
    step,
    max,
    disabled,
    dataCy,
    leading,
    trailing,
}: NumberInputProps): JSX.Element {
    const inputRef = useRef<HTMLInputElement>(null);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (value !== undefined && validator) {
            setHasError(!validator(value));
        } else {
            setHasError(false);
        }
    }, [value, validator]);

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
        if ((event.key === 'Backspace' || event.key === 'Delete') && value?.toString().length === 1) {
            setHasError(false);
            onChange(0);
        }
    }

    function handleChange(e: ChangeEvent<HTMLInputElement>): void {
        const val = e.target.value ? parseFloat(e.target.value.replace(/^0+(?=\d)/, '')) : undefined;
        if (val === undefined || (min !== undefined && val < min) || (max !== undefined && val > max)) return;
        if (validator) {
            setHasError(!validator(val));
        } else {
            setHasError(false);
        }

        onChange(val);
    }

    return (
        <div className={classNames(className, {'is-error': hasError})}>
            {leading && (
                <Button className="c-sidepanel__input__leading" leading={leading} size="xs" variant="ghost" square disablePointerEvents />
            )}
            <input
                className="c-sidepanel__input_field"
                type="number"
                min={min}
                step={step}
                max={max}
                ref={inputRef}
                placeholder={placeholder}
                value={value !== undefined ? value.toString().replace(/^0+(?=\d)/, '') : ''}
                onChange={handleChange}
                onBlur={onBlur ? () => onBlur(value) : undefined}
                onKeyDown={handleKeyDown}
                onKeyUp={onKeyUp}
                disabled={disabled}
                data-cy={dataCy}
            />
            <div className="c-sidepanel__input__trailing">{trailing}</div>
        </div>
    );
}

export default NumberInput;
